import {Component, DestroyRef, inject, input, OnInit} from '@angular/core';
import {PageTitleLayoutComponent} from "../../components/page-title-layout/page-title-layout.component";
import {ProgressSpinner} from "primeng/progressspinner";
import {Router} from "@angular/router";
import {distinctUntilChanged, fromEvent, map, merge, NEVER, of, switchMap, timer} from "rxjs";
import {MessageService} from "primeng/api";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AuthService} from "../../services/auth/auth.service";
import {Location} from "@angular/common";
import {UserService} from "../../services/user.service";

const hasDocument = typeof document !== 'undefined';
const hasWindow = typeof window !== 'undefined';

@Component({
  standalone: true,
  imports: [
    PageTitleLayoutComponent,
    ProgressSpinner
  ],
  templateUrl: './callback.component.html',
  styleUrl: './callback.component.scss'
})
export class CallbackComponent implements OnInit {
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly location = inject(Location);
  public readonly code = input<string>();
  private readonly initial$ = hasDocument && hasWindow ? of(!document.hidden && document.hasFocus()) : of(true);

  // 2) Tab visibility changes (e.g. user switches to another tab)
  private readonly visibility$ = hasDocument
    ? fromEvent(document, 'visibilitychange').pipe(
      map(() => !document.hidden)
    )
    : NEVER;

  // 3) Window focus/blur (e.g. user Alt-Tabs to another app)
  private readonly focus$ = hasWindow
    ? fromEvent(window, 'focus').pipe(map(() => true))
    : NEVER;
  private readonly blur$  = hasWindow
    ? fromEvent(window, 'blur').pipe(map(() => false))
    : NEVER;

  // Merge them and only emit when the “active” boolean actually changes
  private readonly active$ = merge(this.initial$, this.visibility$, this.focus$, this.blur$).pipe(
    distinctUntilChanged()
  );

  ngOnInit() {
    const code = this.code();
    const user = this.userService.user();
    if (code && (!user || user.status === 'unauthorized')) {
      this.codeReceived(code);
    } else {
      this.router.navigate(['/'], {replaceUrl: true});
    }

  }

  async codeReceived(code: string) {
    const [path] = this.router.url.split('?');
    this.location.replaceState(path);
    this.authService.onFederatedLoginCallback(code);
  }

  constructor() {
    this.active$.pipe(
      switchMap((value) => {
          return value
            ? timer(12_000)
            : NEVER
        }
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      () => {
        console.error('Authentication error after 15s');
        this.messageService.add(
          {
            severity: 'error',
            summary: 'Authentication Error',
            detail: 'Authentication failed, please try again later.',
            life: 10000
          }
        )
        this.router.navigate(['/'], {replaceUrl: true});
      }
    );
  }
}
